/**
 * Playbook scripts — versioned sequences the autopilot runs over the synthetic case
 * (spec §18). Steps reference REAL seed node ids + registry operator ids. Honest:
 * operators/evidence whose backend isn't live yet render as job chips with their true
 * status (never faked output); the deterministic spine (stages 1–3) is genuine.
 */
export type PBStepKind = 'stage' | 'focus' | 'operator' | 'job' | 'courtSafe';

export interface PBStep {
  kind: PBStepKind;
  n?: number;               // stage
  nodeId?: string;          // focus / operator target
  opId?: string;            // operator id (O1..O11)
  label?: string;           // job chip label
  message?: string;         // job chip message (its real binding/status)
  on?: boolean;             // court-safe
  hold?: number;            // pacing override (ms)
}

export interface PBAct { title: string; sub: string; steps: PBStep[]; }

/** The five-act demo arc: NCRP → national linkage. */
export const NCRP_FIVE_ACT: PBAct[] = [
  {
    title: 'Act 1 · Intake',
    sub: 'NCRP complaint + the frozen account’s statement land on the canvas',
    steps: [
      { kind: 'courtSafe', on: false, hold: 300 },
      { kind: 'stage', n: 1 },
      { kind: 'focus', nodeId: 'account:50100' },
      { kind: 'job', label: 'E8 · NCRP complaint SYN-0147', message: 'LIVE · ncrp_complaint.v1 client parser' },
      { kind: 'job', label: 'E1 · HDFC ••5100 statement', message: 'LIVE · bank-analyser /api/cases/{id}/statements' },
    ],
  },
  {
    title: 'Act 2 · The frozen account',
    sub: 'Stitch the money by UTR, name the exit, draft the notice',
    steps: [
      { kind: 'focus', nodeId: 'account:50100' },
      { kind: 'operator', opId: 'O1', nodeId: 'account:50100' },
      { kind: 'operator', opId: 'O2', nodeId: 'account:50100' },
      { kind: 'operator', opId: 'O9', nodeId: 'account:50100' },
    ],
  },
  {
    title: 'Act 3 · The crypto leg',
    sub: 'Auto-Investigate the off-ramp wallet to the exchange deposit',
    steps: [
      { kind: 'stage', n: 3 },
      { kind: 'focus', nodeId: 'TRX:TRc9k6' },
      { kind: 'operator', opId: 'O3', nodeId: 'TRX:TRc9k6' },
      { kind: 'focus', nodeId: 'TRX:binance_dep', hold: 1400 },
    ],
  },
  {
    title: 'Act 4 · Identity fusion',
    sub: 'Correlate the trader handle, then flip Court-Safe to show the deterministic spine',
    steps: [
      { kind: 'stage', n: 4 },
      { kind: 'focus', nodeId: 'ig:@kumar_otc' },
      { kind: 'operator', opId: 'O6', nodeId: 'ig:@kumar_otc' },
      { kind: 'courtSafe', on: true, hold: 1800 },
      { kind: 'courtSafe', on: false, hold: 900 },
    ],
  },
  {
    title: 'Act 5 · National linkage',
    sub: 'Fan-in across rails, cross-case match, export the case package',
    steps: [
      { kind: 'stage', n: 5 },
      { kind: 'focus', nodeId: 'id:rkumar' },
      { kind: 'job', label: 'A1 · Cross-Rail Fan-In', message: 'BUILD · needs unified case graph (§CG)' },
      { kind: 'operator', opId: 'O8', nodeId: 'TRX:TRc9k6' },
      { kind: 'focus', nodeId: 'TRX:binance_dep', hold: 1400 },
      { kind: 'operator', opId: 'O10', nodeId: 'id:rkumar' },
    ],
  },
];
